import React, {useEffect, useRef, useState} from 'react';
import {RouteComponentProps, useHistory} from 'react-router-dom';
import {
    IonAlert,
    IonButtons,
    IonCol,
    IonGrid,
    IonHeader,
    IonPage, IonRow, isPlatform,
    useIonViewWillEnter,
    useIonViewWillLeave
} from '@ionic/react';
import {
    StyledEmptyContainer,
    StyledIonContentCameraPreview,
    StyledIonTitle,
    StyledIonToolbar
} from '../../components/content/content.style';
import {StyledButton, StyledHeaderButton, StyledHeaderButtonImage} from '../../components/button/button.style';
import ArrowLeftImage from '../../assets/images/arrow-left.svg';
import CameraPreviewImage from '../../assets/images/takePhoto-small.png';
import {useTranslation} from 'react-i18next';
import {StyledContent} from '../takePhotoPage/takePhotoPage.style';
import CircleImage, {ImageTypes} from "../../components/circleImage/circleImage.component";
import {Links} from "../links";
import {findContainerByNumber} from "../../services/reportFactory.service";
import {
    checkCameraPermission,
    openSettings,
    startContinuousScan,
    makeWebViewTransparent,
    restoreWebView,
    BARCODE_1D_AND_QR_FORMATS,
    ContinuousScanHandle,
} from "@services/barcodeScanner.service";

interface RouterProps {
    orderId: string;
    specificationId: string;
    logisticContainerShowAlerts: string;
    logisticContainerUsePrefix: string;
    logisticContainerPrefix: string | undefined;
    internalCostReport?: string;
}

interface ManualContainerProps extends RouteComponentProps<RouterProps> {
}

const QRCodeContainerPage: React.FC<ManualContainerProps> = ({match}) => {

        const {t} = useTranslation();
        const history = useHistory();

        const handleBack = () => {
            history.goBack();
        };

        const {
            params: {
                orderId,
                specificationId,
                logisticContainerShowAlerts,
                logisticContainerUsePrefix,
                logisticContainerPrefix,
                internalCostReport
            }
        } = match;

        const [topEdge, updateTopEdge] = useState<number | undefined>(undefined);
        const [showError, setShowError] = useState<boolean>(false);
        const [error, setError] = useState<string>();
        const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);

        const scanHandleRef = useRef<ContinuousScanHandle | null>(null);

        useEffect(() => {
            updateHeight();
        })

        const startScan = async () => {
            makeWebViewTransparent();

            const handle = await startContinuousScan(BARCODE_1D_AND_QR_FORMATS, async (rawValue) => {
                if (rawValue) {

                    let labelName = rawValue.charAt(0);
                    if (logisticContainerUsePrefix == 'true') {
                      let prefixes: string[] = [];
                      try {
                        prefixes = logisticContainerPrefix ? decodeURIComponent(logisticContainerPrefix ?? "").split(",") : [];
                      } catch (e) {
                        if (logisticContainerPrefix) {
                          prefixes = [logisticContainerPrefix];
                        }
                      }

                      if (Array.isArray(prefixes) && prefixes.includes(labelName)) {
                        const matchedPrefix = prefixes.find(prefix => rawValue.startsWith(prefix));

                        if (matchedPrefix) {
                          let error = await handleScan(rawValue.replace(matchedPrefix, ''));

                          if (logisticContainerShowAlerts == 'true') {
                            if (typeof error == 'string') {
                              if (error == 'CONTAINER_NOT_IN_THIS_ORDER') {
                                setError(t('reportsTab.orders.scanContainerPage.error.container_not_in_this_order'))
                              } else if (error == 'CONTAINER_DONE') {
                                setError(t('reportsTab.orders.scanContainerPage.error.container_done'))
                              } else if (error == 'NO_CONTAINER') {
                                setError(t('reportsTab.container.manualContainerFind.error.no_container'))
                              } else {
                                setError(t('reportsTab.orders.scanSpecificationPage.error.common'))
                              }

                              stopScan();
                              setShowError(true);
                            }
                          }
                        }
                      }
                    } else {
                        let error = await handleScan(rawValue);
                        if (logisticContainerShowAlerts == 'true') {
                            if (typeof error == 'string') {
                                if (error == 'CONTAINER_NOT_IN_THIS_ORDER') {
                                    setError(t('reportsTab.orders.scanContainerPage.error.container_not_in_this_order'))
                                } else if (error == 'CONTAINER_DONE') {
                                    setError(t('reportsTab.orders.scanContainerPage.error.container_done'))
                                } else if (error == 'NO_CONTAINER') {
                                    setError(t('reportsTab.container.manualContainerFind.error.no_container'))
                                } else {
                                    setError(t('reportsTab.orders.scanSpecificationPage.error.common'))
                                }

                                stopScan();
                                setShowError(true);
                            }
                        }
                    }
                }
            });

            scanHandleRef.current = handle;
        };

        const handleScan = async (val: any) => {
            let data = await findContainerByNumber(val, orderId);

            if (typeof data == 'string') {
                return data;
            }

            if (data) {
                history.push(`${Links.report}/${orderId}/${specificationId}/0/0/${data}/${internalCostReport}`);
            }
        }


        const checkPermission = async () => {
            return await checkCameraPermission();
        }

        const start = async () => {
            let hasPermission = await checkPermission();
            if (!hasPermission) {
                setShowPermissionAlert(true);
            }
            startScan();
            setShowError(false);
            setError('');
        }

        useIonViewWillEnter(() => {
            if (isPlatform('ios') || isPlatform('android')) {
                start();
            }
        });

        const stopScan = async () => {
            await scanHandleRef.current?.stop();
            scanHandleRef.current = null;
            restoreWebView();
        };

        useIonViewWillLeave(() => {
            if (isPlatform('ios') || isPlatform('android')) {
                stopScan();
            }
        });

        const updateHeight = () => {
            if (header.current?.clientHeight == 0) {
                setTimeout(updateHeight);
            } else {
                updateTopEdge(header.current?.clientHeight);
            }
        };

        const header = useRef<HTMLIonHeaderElement>(null);

        return (
            <IonPage>
                <StyledIonContentCameraPreview>
                    <img className="background" src={CameraPreviewImage}/>
                    <IonHeader ref={header} className="ion-no-border">
                        <StyledIonToolbar>
                            <IonButtons slot="start">
                                <StyledHeaderButton onClick={() => handleBack()}>
                                    <StyledHeaderButtonImage src={ArrowLeftImage}></StyledHeaderButtonImage>
                                </StyledHeaderButton>
                            </IonButtons>
                            <StyledIonTitle>{t('reportsTab.orders.scanContainerPage.title')}</StyledIonTitle>
                        </StyledIonToolbar>
                        <div className="sub-title-light">{t('reportsTab.orders.scanContainerPage.subtitle')}</div>
                    </IonHeader>
                    <StyledContent>
                        <div className="target">
                            {
                                showError &&
                                <StyledEmptyContainer className="preview">
                                    <CircleImage onClick={async (e) => {
                                        start();
                                    }} image={ImageTypes.QR} color="red"/>
                                </StyledEmptyContainer>
                            }
                        </div>
                        <div className={'action-buttons empty multiple-buttons'}>
                            <IonGrid>
                                {showError && <IonRow>
                                    <IonCol size="12">
                                        <p className="errorMessage">{error}</p>
                                    </IonCol>
                                </IonRow>}
                                <IonRow>
                                    <IonCol size="12"><StyledButton
                                        onClick={() => history.push(Links.manualContainerFind + '/' + orderId + '/' + specificationId + '/' + internalCostReport)}>{t('reportsTab.orders.scanContainerPage.manualButton')}</StyledButton></IonCol>
                                </IonRow>
                            </IonGrid>
                        </div>
                    </StyledContent>
                </StyledIonContentCameraPreview>
                <IonAlert
                    isOpen={showPermissionAlert}
                    onDidDismiss={() => setShowPermissionAlert(false)}
                    header={t('common.permissions.alert')}
                    buttons={[
                        {
                            text: t('common.alertCancel'),
                            role: 'cancel',
                            cssClass: 'secondary',
                            handler: () => {
                                setShowPermissionAlert(false);
                            },
                        },
                        {
                            text: t('common.permissions.goToAppSettings'),
                            handler: async () => {
                                setShowPermissionAlert(false);
                                openSettings();
                            }
                        }
                    ]}
                />
            </IonPage>
        );
    }
;

export default QRCodeContainerPage;
